import React, { useState, useEffect } from 'react';
import { Copy, Check, X, Share2 } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Post = {
  platform: string;
  content: string;
  hashtags?: string[];
};

type ContentDisplayProps = {
  posts: Post[];
  onClose: () => void;
};

export function ContentDisplay({ posts, onClose }: ContentDisplayProps) {
  const navigate = useNavigate();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [postedIndex, setPostedIndex] = useState<number | null>(null);
  const [platformMap, setPlatformMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchPlatforms = async () => {
      const { data, error } = await supabase
        .from('platforms')
        .select('name, share_url');

      if (error) {
        console.error('Error fetching platforms', error);
      } else {
        const map = data.reduce((acc, item) => {
          acc[item.name] = item.share_url;
          return acc;
        }, {} as Record<string, string>);
        setPlatformMap(map);
      }
    };

    fetchPlatforms();
  }, []);

  const handleCopy = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getPlatformShareUrl = (platform: string, content: string) => {
    const template = platformMap[platform];
    if (!template) return '';
    const encodedUrl = encodeURIComponent(window.location.origin);
    const encodedText = encodeURIComponent(content);
    return template.replace('{{url}}', encodedUrl).replace('{{text}}', encodedText);
  };

  const handlePost = async (content: string, platform: string, index: number) => {
    try {
      const shareUrl = getPlatformShareUrl(platform, content);
      if (shareUrl) {
        window.open(shareUrl, '_blank');
        setPostedIndex(index);
        setTimeout(() => setPostedIndex(null), 2000);
      }
    } catch (err) {
      console.error('Failed to post:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#004060]">Your Content is Ready 🎉</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {!posts || posts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No content available at the moment.</p>
            </div>
          ) : (
            posts.map((post, index) => (
              <div
                key={index}
                className="p-6 rounded-lg border border-gray-200 hover:border-[#236caf] transition-colors duration-200 bg-gradient-to-r from-white to-blue-50/30"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleCopy(post.content, index)}
                        className={cn(
                          "inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full transition-colors duration-200",
                          copiedIndex === index
                            ? "bg-green-50 text-green-600"
                            : "bg-blue-50 text-[#236caf] hover:bg-blue-100"
                        )}
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Post
                          </>
                        )}
                      </button>

                      {platformMap[post.platform] ? (
                        <button
                          onClick={() => handlePost(post.content, post.platform, index)}
                          className={cn(
                            "inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full transition-colors duration-200",
                            postedIndex === index
                              ? "bg-green-50 text-green-600"
                              : "bg-blue-50 text-[#236caf] hover:bg-blue-100"
                          )}
                        >
                          {postedIndex === index ? (
                            <>
                              <Check className="w-4 h-4" />
                              Opening...
                            </>
                          ) : (
                            <>
                              <Share2 className="w-4 h-4" />
                              Share on {post.platform.split('-')[0]}
                            </>
                          )}
                        </button>
                      ) : (
                        <div
                          className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-400 cursor-not-allowed"
                          title="Sharing not available for this platform"
                        >
                          <Share2 className="w-4 h-4" />
                          Share Unavailable
                        </div>
                      )}
                    </div>
                  </div>

                  {post.hashtags && post.hashtags.length > 0 && (
                    <button
                      onClick={() => handleCopy(post.hashtags?.join(' ') || '', index)}
                      className="text-sm px-3 py-1 bg-gray-50 text-gray-600 rounded-full hover:bg-gray-100 transition-colors duration-200"
                    >
                      Copy Hashtags
                    </button>
                  )}
                </div>

                <div
                  className="text-gray-700 mb-4 leading-relaxed p-4 bg-gray-50 rounded-lg cursor-text"
                  onClick={() => handleCopy(post.content, index)}
                >
                  {post.content.split('\n\n').map((para, i) => (
                    <p key={i} className="mb-4">{para}</p>
                  ))}
                </div>

                {post.hashtags && post.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {post.hashtags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-blue-50 text-[#236caf] rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <Button onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}